// One job a machine holds for the viewing account (#6745, epic #6552): the entry of
// `GET /v1/fleet/utilization`'s `boxes[].jobs` and, minus its `usage`, of the `machines`
// stream topic's row (`MachinesTopicRowSchema`, `./sse-frames.ts`). The REST merge that
// produces it is `boxJobsView` (`apps/api/src/routes/fleet-utilization-jobs.ts`).
//
// USAGE IS NOT ON THE TOPIC ROW. It moves on every presence beat, and a beat field never
// re-emits a machine row (`machineStateVersion` stays beat-blind, as for `stats`); the REST
// read carries it and the page's slow refresh keeps it current.

import { z } from 'zod';
import { AgentModeKeySchema } from '../entities/agent-mode';
import { PromptRoleSchema } from '../entities/system-prompt';
import { TaskLlmPreferenceSchema } from '../value-objects/task-envelope';
import { TaskClassSchema } from '../value-objects/task-envelope/task-class';

/**
 * Where a held job stands, as the card reads it. First match wins in `boxJobsView`:
 * `paused` (a stored fact, offline too), `pausing` (a pause requested on a started run),
 * then by the machine's beat: `working` (the beat reports it), `starting` (held, not yet on
 * the beat), `last_known` (the machine has no live check-in, so nothing is a reading).
 */
export const MACHINE_JOB_STATES = [
  'working',
  'starting',
  'last_known',
  'pausing',
  'paused',
] as const;
export type MachineJobState = (typeof MACHINE_JOB_STATES)[number];

/**
 * The stored task status a job row is read from: the box-held pair, plus the two rows
 * pinned to the machine by a pause (#6748): `paused`, and a resumed `pending` that only its
 * own machine may claim.
 */
export const MACHINE_JOB_STATUSES = ['assigned', 'in_progress', 'paused', 'pending'] as const;
export const MachineJobStatusSchema = z.enum(MACHINE_JOB_STATUSES);
export type MachineJobStatus = z.infer<typeof MachineJobStatusSchema>;

/**
 * The plan (epic) the job's task is a slice of, when it is one of THIS account's plans.
 * `title` is the plan's headline and `done` / `total` the same tally `plan list` reports
 * (`planEpicsByTaskIds`, `@developerz/db`); `running` counts its slices held right now.
 *
 * `paused_at` and `llm` (#6753) are the plan row's own epic pause mark and stored model,
 * the two facts the card's epic controls read. Both are REQUIRED, null saying "not paused"
 * and "Automatic": a fully paused epic has `running: 0`, so without `paused_at` the card
 * could not tell it from a quiet one and would drop its Resume. The dashboard parses the
 * `machines` topic's rows with this schema, so for the length of a roll a new bundle
 * talking to an old api pod drops that topic's rows (`account-stream-frames.ts` accepts
 * that for an added required field).
 */
export const MachineJobEpicSchema = z.object({
  plan_id: z.string(),
  title: z.string(),
  done: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  running: z.number().int().nonnegative(),
  /** `plans.paused_at`, ISO; null when the epic is not paused (#6751's plan pause door). */
  paused_at: z.string().nullable(),
  /** `plans.llm`, the epic's model (#7012); null is Automatic. */
  llm: TaskLlmPreferenceSchema.nullable(),
});
export type MachineJobEpic = z.infer<typeof MachineJobEpicSchema>;

/** One job, without its live usage: the `machines` topic's entry. */
export const MachineJobRowSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  repo: z.string().nullable(),
  run_id: z.string().nullable(),
  status: MachineJobStatusSchema,
  /** The run's FIRST event; null before the run emits one, and on a paused row. */
  started_at: z.string().nullable(),
  /** The scheduling class, as `current_class` reads it. */
  class: TaskClassSchema,
  /** `promptRoleForEnvelope` over the stored origin; null when the origin does not parse. */
  role: PromptRoleSchema.nullable(),
  /**
   * The capability group the job moves with (#6769): `AGENT_MODE_KEY_FOR_ORIGIN` over the
   * stored origin, the key a group move (`{ kind: "group", id }`) and the machine role
   * switches name; null for an origin no switch holds or that does not parse. It differs from
   * `role` (the PROSE role) for `pr_babysit`, `kb_fold`, `plan_author` and `setup`. Optional
   * on the parse: a server older than #6769 sends none.
   */
  mode_key: AgentModeKeySchema.nullable().optional(),
  state: z.enum(MACHINE_JOB_STATES),
  /** The lane fact `isPausableTask`; whether the MACHINE can pause is its beat's verbs. */
  pausable: z.boolean(),
  epic: MachineJobEpicSchema.nullable(),
});
export type MachineJobRow = z.infer<typeof MachineJobRowSchema>;

/**
 * The job's live usage off its machine's beat (`JobUsageSchema`, #6743), snake_case. Each
 * reading is null when it was not measured; the share is of the WHOLE machine.
 */
export const MachineJobUsageSchema = z.object({
  cpu_pct: z.number().nullable(),
  mem_bytes: z.number().nullable(),
  disk_bytes: z.number().nullable(),
});
export type MachineJobUsage = z.infer<typeof MachineJobUsageSchema>;

/** One job as `GET /v1/fleet/utilization` serves it: the row plus its usage, or null. */
export type MachineJob = MachineJobRow & { usage: MachineJobUsage | null };
